import React, { useState } from "react";
import { Box, Typography, Grid, TextField, Button, Divider, InputAdornment, IconButton, Checkbox, FormControlLabel } from "@mui/material";
import CustomAlert from "./CustomAlert";
import { useNavigate } from 'react-router-dom';
import { showDeleteConfirmation } from "./deletemsg";
import CustomDialog from "./CustomDialog";
import { Eye, EyeOff } from "lucide-react";

const getValue = (obj, path) =>
  path.split('.').reduce((acc, key) => (acc ? acc[key] : ''), obj);

const setValue = (obj, path, value) => {
  const keys = path.split('.');
  const lastKey = keys.pop();
  const nested = keys.reduce((acc, key) => {
    if (!acc[key]) acc[key] = {};
    return acc[key];
  }, obj);
  nested[lastKey] = value;
};

const ProfileForm = ({
  title,
  formData,
  setFormData,
  fields,
  isEditing,
  onSave,
  onToggleEdit,
  showSaveButton = true,
  isInterlocuteurTab = false,
  onChangePassword
}) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const authToken = localStorage.getItem("authToken");
  const userId = localStorage.getItem("userId");
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');
  const navigate = useNavigate();
  const [finalDialogOpen, setFinalDialogOpen] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const handlePasswordChange = () => {
    if (newPassword !== confirmPassword) {
      alert("Les mots de passe ne correspondent pas.");
      return;
    }

    const updatedFormData = { ...formData, newPassword: newPassword };
    setFormData(updatedFormData);

    onChangePassword(newPassword); 
  };

  const numericFields = ["utilisateur.phone", "ice", "tranche"];  
  const handleInputChange = (e, path) => {
    let value = e.target.value;

    if (numericFields.includes(path)) {
      value = value.replace(/[^0-9]/g, "");
    }

    const updatedFormData = { ...formData };
    setValue(updatedFormData, path, value);
    setFormData(updatedFormData);
  };


  const handleDeleteAccount = () => {
    showDeleteConfirmation({
      onConfirm: async () => {
        try {
          const res = await fetch(`${API_BASE}/api/auth/supprimer-profil`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({ userId }),
          });
  
          const data = await res.json();
  
          if (res.ok) {
            setFinalDialogOpen(true);
            setTimeout(() => {
              localStorage.clear();
              navigate("/");
            }, 2000);
          } else {
            setAlertMessage(data.message || "Erreur.");
            setAlertSeverity("error");
            setAlertOpen(true);
          }
        } catch (error) {
          console.error(error);
          alert("Erreur lors de la demande de suppression.");
        }
      },
  
    });
  };
  
  
  return (
    <Box sx={{ p: 4 }}>
      <CustomDialog
        open={finalDialogOpen}
        onClose={() => setFinalDialogOpen(false)}
        title="Suppression du compte"
        content="Votre demande a bien été prise en compte. Vous allez être déconnecté."
        align="center"
      />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">{title}</Typography>
        <Box display="flex" gap={2}>
          <Button variant="outlined" onClick={onToggleEdit}>
            {isEditing ? "Annuler" : "Éditer"}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeleteAccount}
          >
            Supprimer mon compte
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />
      
      <Grid container spacing={2}>
        {fields.map(({ label, name, type = "text", multiline = false, rows = 2, readOnly = false, onKeyPress }) => (
          <Grid item xs={12} size={label === "Description" ? 12 : 6} key={name}>
            <TextField
              label={label}
              fullWidth
              type={type}
              multiline={multiline}
              rows={rows}
              value={getValue(formData, name) || ""}
              onChange={(e) => handleInputChange(e, name)}  
              onKeyPress={onKeyPress}
              variant="outlined"
              InputProps={{
                readOnly: readOnly || !isEditing || name === "utilisateur.email", 
              }}
            />
          </Grid>
        ))}
      </Grid>

    <Box mt={2}>
      <FormControlLabel
        control={
          <Checkbox
            checked={!!getValue(formData, 'utilisateur.receiveMail')}
            onChange={(e) => {
              const updatedFormData = { ...formData };
              setValue(updatedFormData, 'utilisateur.receiveMail', e.target.checked);
              setFormData(updatedFormData);
            }}
            color="primary"
            disabled={!isEditing} 
          />
        }
        label="Recevoir les notifications par email"
      />
    </Box>
      {isEditing && showSaveButton && (
        <Box mt={3} textAlign="right" display="flex" justifyContent="flex-end">
          <Button variant="contained" color="primary" onClick={onSave}>
            Sauvegarder
          </Button>
        </Box>
      )}

      {isInterlocuteurTab && isEditing && (
        <Box mt={3}>
          <Typography variant="h6" fontWeight="bold">Changer le mot de passe</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} size={6}>
              <TextField
                label="Nouveau mot de passe"
                fullWidth
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNewPassword(v => !v)} edge="end">
                        {showNewPassword ? <Eye /> : <EyeOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} size={6}>
              <TextField
                label="Confirmer mot de passe"
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(v => !v)} edge="end">
                        {showConfirmPassword ? <Eye /> : <EyeOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          <Box mt={2} textAlign="right">
            <Button
              variant="contained"
              color="primary"
              onClick={handlePasswordChange}
            >
              Mettre à jour le mot de passe
            </Button>
          </Box>
        </Box>
      )}

    </Box>
  );
};

export default ProfileForm;
